import { useQuery } from '@tanstack/react-query';
import { customerAPI, driverAPI } from '@matix/api-client';
import type { Area, Customer, Driver } from '@matix/types';

/**
 * Phase 9 — query hooks for the Delivery modal.
 *
 * Drivers don't change often during a shift, so we cache for 5 minutes.
 * Customer search is debounced at the input level; addresses come back as
 * part of the customer detail response so a single GET feeds the picker.
 */

const DRIVERS_KEY = ['drivers'] as const;

export function useDrivers() {
  return useQuery<{ data: Driver[] }>({
    queryKey: DRIVERS_KEY,
    queryFn: () => driverAPI.list(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomerSearch(q: string) {
  return useQuery({
    queryKey: ['customer-search', q],
    queryFn: () => customerAPI.search(q, 'cash', 10),
    enabled: true,
    staleTime: 30 * 1000,
  });
}

export function useCustomerDetail(id: number | null) {
  return useQuery<Customer | null>({
    queryKey: ['customer-detail', id],
    queryFn: async () => (id == null ? null : customerAPI.getById(id, 'cash')),
    enabled: id != null,
    staleTime: 30 * 1000,
  });
}

/**
 * Full list of GL_Area entries, cached for an hour. Areas rarely change so
 * the cashier rarely refetches; clearing the cache happens naturally on a
 * page reload or via a manual queryClient.invalidate.
 */
export function useAreas() {
  return useQuery<{ data: Area[] }>({
    queryKey: ['areas'],
    queryFn: () => customerAPI.listAreas(),
    staleTime: 60 * 60 * 1000,
  });
}
